import { Field } from "formik";
import { useSchemeIssueMappingTree } from "../../schemeIssueMapping/hooks/useSchemeIssueMapping";

interface IssueCascadeFieldsProps {
	index: number;
	isView: boolean;
	disabled?: boolean;
	issue: {
		issue_type_id: number | null;
		issue_category_id: number | null;
		issue_sub_category_id: number | null;
	};
	setFieldValue: any;
	schemeId?: number | null;
	typeColClass?: string;
	categoryColClass?: string;
	subCategoryWrapperClass?: string;
	// Some scheme forms (e.g. PMAY-G) also mirror the chosen sub category's
	// name into a free-text "description" field — set only for those.
	descriptionFieldName?: string;
}

// Type → Category → Sub Category cascade for a Scheme Data Entry "Para
// Particulars" issue row, driven by that scheme's mapping tree
// (scheme_issue_mappings) instead of the legacy scheme.issue_type_id columns.
// A scheme mapped without a type (its tree has directCategories but no
// types) skips the Type dropdown entirely and goes straight to Category.
export const IssueCascadeFields = ({
	index,
	isView,
	disabled,
	issue,
	setFieldValue,
	schemeId,
	typeColClass = "col-md-4 fv-row",
	categoryColClass = "col-md-4 fv-row",
	subCategoryWrapperClass = "col-md-12 fv-row",
	descriptionFieldName,
}: IssueCascadeFieldsProps) => {
	const { tree } = useSchemeIssueMappingTree(schemeId);
	const hasType = tree.types.length > 0;
	const isDisabled = isView || Boolean(disabled);

	const selectedType = hasType
		? tree.types.find((t) => Number(t.id) === Number(issue.issue_type_id))
		: null;
	const categoryOptions = hasType
		? selectedType?.categories ?? []
		: tree.directCategories;
	const selectedCategory = categoryOptions.find(
		(c) => Number(c.id) === Number(issue.issue_category_id),
	);
	const subCategoryOptions = selectedCategory?.sub_categories ?? [];

	return (
		<>
			{hasType && (
				<div className={typeColClass}>
					<label className="form-label fw-bold">Type</label>
					<Field
						as="select"
						name={`issues.${index}.issue_type_id`}
						className="form-select form-select-solid"
						disabled={isDisabled}
						onChange={(e: any) => {
							const val = Number(e.target.value) || null;
							setFieldValue(`issues.${index}.issue_type_id`, val);
							setFieldValue(`issues.${index}.issue_category_id`, null);
							setFieldValue(`issues.${index}.issue_sub_category_id`, null);
							setFieldValue(`issues.${index}.code_no`, "");
						}}
					>
						<option value="">Select Type</option>
						{tree.types.map((t) => (
							<option key={t.id} value={t.id}>
								{t.name}
							</option>
						))}
					</Field>
				</div>
			)}
			<div className={categoryColClass}>
				<label className="form-label fw-bold">Category</label>
				<Field
					as="select"
					name={`issues.${index}.issue_category_id`}
					className="form-select form-select-solid"
					disabled={isDisabled || (hasType && !issue.issue_type_id)}
					onChange={(e: any) => {
						const val = Number(e.target.value) || null;
						setFieldValue(`issues.${index}.issue_category_id`, val);
						setFieldValue(`issues.${index}.issue_sub_category_id`, null);
						setFieldValue(`issues.${index}.code_no`, "");
					}}
				>
					<option value="">Select Category</option>
					{categoryOptions.map((c) => (
						<option key={c.id} value={c.id}>
							{c.name}
						</option>
					))}
				</Field>
			</div>
			<div className={subCategoryWrapperClass}>
				<label className="form-label fw-bold">Sub-Category</label>
				<Field
					as="select"
					name={`issues.${index}.issue_sub_category_id`}
					className="form-select form-select-solid"
					disabled={isDisabled || !issue.issue_category_id}
					onChange={(e: any) => {
						const val = Number(e.target.value) || null;
						setFieldValue(`issues.${index}.issue_sub_category_id`, val);
						const sub = subCategoryOptions.find((s) => Number(s.id) === val);
						setFieldValue(`issues.${index}.code_no`, sub?.code || "");
						if (descriptionFieldName) {
							setFieldValue(
								`issues.${index}.${descriptionFieldName}`,
								sub?.name || "",
							);
						}
					}}
				>
					<option value="">Select Sub-Category</option>
					{subCategoryOptions.map((s) => (
						<option key={s.id} value={s.id}>
							{s.name}
						</option>
					))}
				</Field>
			</div>
		</>
	);
};
